import { FiTv, FiRadio, FiBookOpen, FiFeather, FiTrendingUp, FiMoon, FiGlobe, FiCode, FiSearch, FiImage } from "react-icons/fi";
import { FaSkull, FaClock, FaFistRaised, FaFutbol } from "react-icons/fa";
import { GiCrossedSwords } from "react-icons/gi";

// Single source of truth for everything we ship.
//
// Home and Products both used to carry their own copy of this list, which is how
// Chrono Arena ended up with two different descriptions and how a new release meant
// editing the same product in two files. Both pages now read from here.
//
// `category` drives the grouping on both pages. Adding a category means adding it to
// CATEGORIES below; nothing else needs to change.
//
// The Tailwind colour names are written out in full on purpose. Tailwind scans source
// files for literal class strings, so `text-accent-amber` has to appear verbatim
// somewhere in src/ or the class never reaches the stylesheet.

export const CATEGORIES = [
  {
    id: 'videogames',
    name: 'Videogames',
    blurb: 'PC games built the old way: buy once, install, play. No launchers, no season passes.',
  },
  {
    id: 'software',
    name: 'Software',
    blurb: 'Native Windows applications that do one job properly and stay out of your way.',
  },
  {
    id: 'saas',
    name: 'SaaS',
    blurb: 'Hosted, multi-tenant platforms. You log in, you get your own space, and there is nothing to install.',
  },
  {
    id: 'extensions',
    name: 'Extensions',
    blurb: 'Browser tools that add something useful to the sites you already work in.',
  },
  {
    id: 'launches',
    name: 'Launches',
    blurb: 'Digital toolkits released as launches: guides, prompt systems and web apps for creators, each with its own site.',
  },
];

export const PRODUCTS = [
  {
    id: 'rudis',
    category: 'videogames',
    name: 'RUDIS: Blood for Freedom',
    tagline: 'Physics gladiator duels',
    short: 'Step onto the sand of a Roman arena. Every blow is simulated, every wound counts, and the crowd decides who walks out.',
    desc: 'RUDIS: Blood for Freedom is a physics-driven gladiator duel game for PC. Choose one of 16 fighters across the classic armaturae, from the Thracian and the murmillo to the retiarius with his net and trident, and fight one-on-one where every cut, thrust and shield blow is resolved by the simulation. Win the stands, survive the missio and fight your way to freedom. Local versus for two players. English and Spanish.',
    icon: GiCrossedSwords,
    color: 'text-accent-red',
    bgColor: 'bg-accent-red/10',
    borderColor: 'hover:border-accent-red/50',
    btnColor: 'bg-accent-red hover:bg-accent-red/90',
    link: '/products/rudis',
    price: 'Coming soon',
    priceNote: 'Coming soon',
    year: 'In development',
    platform: 'Windows',
    comingSoon: true,
    store: null,
    storeLink: null,
    stores: [
      { id: 'steam', name: 'Steam', href: null, status: 'soon' },
      { id: 'epic', name: 'Epic Games Store', href: null, status: 'soon' },
    ],
    features: [
      'Physics duels, no canned animations',
      '16 gladiators, classic armaturae',
      'The crowd decides: missio or iugula',
      'A career to win your freedom',
      'Local versus for two players',
      'English and Spanish',
    ],
    frames: [
      { src: '/reel/rudis-1.webp', note: 'Thraex against murmillo, shield to shield' },
      { src: '/reel/rudis-2.webp', note: 'The walk out onto the sand' },
      { src: '/reel/rudis-3.webp', note: 'Morituri te salutant: the salute to the editor' },
      { src: '/reel/rudis-4.webp', note: "Murmillo against provocator below the editor's box" },
      { src: '/reel/rudis-5.webp', note: 'A dull bout: the referee sends in the lorarii' },
      { src: '/reel/rudis-9.webp', note: 'Iugula: the crowd has spoken' },
      { src: '/reel/rudis-6.webp', note: 'The victor and the crowd' },
      { src: '/reel/rudis-7.webp', note: 'Choose your gladiator' },
      { src: '/reel/rudis-8.webp', note: 'The arena' },
    ],
    iconSrc: '/icons/rudis.webp',
    image: '/products/rudis.webp',
  },
  {
    id: 'caseunknown',
    category: 'videogames',
    name: 'CaseUnknown',
    tagline: 'Detective Mystery Game',
    short: 'Step into the detective\'s chair. Interrogate suspects, weigh the evidence, and crack procedurally deep cases.',
    desc: 'CaseUnknown puts you in the detective\'s chair. Read the case file, interview suspects under a ticking clock, follow the evidence and name the culprit. A story-driven mystery game with a career mode that ramps from easy cases to hard ones, timed interrogations and a case map you explore at your own pace. Coming soon to PC and Mac.',
    icon: FiSearch,
    color: 'text-accent-blue',
    bgColor: 'bg-accent-blue/10',
    borderColor: 'hover:border-accent-blue/50',
    btnColor: 'bg-accent-blue hover:bg-accent-blue/90',
    link: '/products/caseunknown',
    price: 'Coming soon',
    priceNote: 'Coming soon',
    year: '2026',
    platform: 'Windows + Mac',
    // No single store yet: this releases across several. `stores` lists them,
    // each marked coming soon until its link is live. `comingSoon` drives the
    // "coming soon" badge on the cards and hides the buy button.
    comingSoon: true,
    store: null,
    storeLink: null,
    stores: [
      { id: 'steam', name: 'Steam', href: null, status: 'soon' },
      { id: 'epic', name: 'Epic Games Store', href: null, status: 'soon' },
      { id: 'apple', name: 'Mac App Store', href: null, status: 'soon' },
      { id: 'microsoft', name: 'Microsoft Store', href: null, status: 'soon' },
    ],
    features: [
      'Story-driven detective cases',
      'Interrogate suspects on a timer',
      'Career mode: easy to hard',
      'Explore the case map freely',
      'Weigh evidence and accuse',
      'Zero tracking or ads',
    ],
    frames: [
      { src: '/reel/caseunknown-1.webp', note: 'The Gilded Frame: a painting walks out of a locked museum' },
      { src: '/reel/caseunknown-2.webp', note: 'Conservation studio, where the forgery was born' },
      { src: '/reel/caseunknown-3.webp', note: 'Security room: the minute the tape lost' },
      { src: '/reel/caseunknown-4.webp', note: 'The vault: follow the canvas, not the story' },
      { src: '/reel/caseunknown-5.webp', note: 'Curator office: clean hands, dirty gloves' },
    ],
    iconSrc: '/icons/caseunknown.svg',
    image: '/reel/caseunknown-1.webp',
  },
  {
    id: 'thenightshift',
    category: 'videogames',
    name: 'The Night Shift: Case Files',
    tagline: 'Neo-noir detective deduction',
    short: 'The night shift is yours, detective. Read the file, catch the forgery, break the alibi, and stamp CHARGE or RELEASE before the clock runs out.',
    desc: 'The Night Shift: Case Files is a neo-noir deduction game where you are the last desk between the accused and the cells. Suspects land in your custody one by one: cross-check their documents, spot forgeries, break alibis and stamp your verdict before the shift clock runs out. A 7-night campaign with recurring characters, boss confrontations and 8 endings shaped by your corruption, your accuracy and your conscience. Fully voiced and subtitled in English and Spanish.',
    icon: FiMoon,
    color: 'text-accent-blue',
    bgColor: 'bg-accent-blue/10',
    borderColor: 'hover:border-accent-blue/50',
    btnColor: 'bg-accent-blue hover:bg-accent-blue/90',
    link: null,
    siteLink: 'https://thenightshift.mediaverselabs.com',
    price: '£4.99',
    priceNote: 'One-time £4.99',
    year: '2026',
    platform: 'Windows + Mac',
    comingSoon: true,
    store: null,
    storeLink: null,
    stores: [
      { id: 'steam', name: 'Steam', href: null, status: 'soon' },
      { id: 'epic', name: 'Epic Games Store', href: 'https://store.epicgames.com/p/the-night-shift-29825e', status: 'live' },
      { id: 'apple', name: 'Mac App Store', href: null, status: 'soon' },
      { id: 'microsoft', name: 'Microsoft Store', href: 'https://apps.microsoft.com/detail/9nwgz48cz9sw', status: 'live' },
    ],
    features: [
      'A 7-night campaign, 8 endings',
      'Spot forgeries and break alibis',
      'Interrogate suspects with evidence',
      'Bribes vs Internal Affairs',
      'Manhunt, boss cases and endless mode',
      'Fully voiced in English and Spanish',
    ],
    frames: [
      { src: '/reel/thenightshift-1.webp', note: 'The desk: cross-check the file before the clock runs out' },
      { src: '/reel/thenightshift-2.webp', note: 'Interrogation: push with evidence, or watch them lawyer up' },
      { src: '/reel/thenightshift-3.webp', note: 'Boss case: four suspects, one crime, one lie to break' },
      { src: '/reel/thenightshift-4.webp', note: 'Manhunt: trace the traits across the city' },
      { src: '/reel/thenightshift-5.webp', note: 'The verdict: charge or release, then live with it' },
    ],
    iconSrc: '/icons/thenightshift.webp',
    image: '/reel/thenightshift-1.webp',
  },
  {
    id: 'iron-fury',
    category: 'videogames',
    name: 'IRON FURY',
    tagline: "Neon-noir beat 'em up",
    short: 'One rainy night, four unlikely heroes, one city to save. A finished 1-2 player side-scrolling brawler made for the couch.',
    desc: "IRON FURY is a neon-noir beat 'em up for 1-2 players. VANTAGE has chipped Greyhaven with HALO, and at 06:00 the BLACKOUT signal will make every citizen obey. Fight through six stages, a highway chase and a glass-lift battle, take down seven bosses, and pull off co-op team attacks with a friend. Four heroes, 32 achievements, three difficulties, English and Spanish.",
    icon: FaFistRaised,
    color: 'text-accent-red',
    bgColor: 'bg-accent-red/10',
    borderColor: 'hover:border-accent-red/50',
    btnColor: 'bg-accent-red hover:bg-accent-red/90',
    link: '/products/iron-fury',
    siteLink: 'https://ironfury.mediaverselabs.com',
    price: '£4.99',
    priceNote: 'One-time £4.99',
    year: '2026',
    platform: 'Windows',
    comingSoon: true,
    store: null,
    storeLink: null,
    stores: [
      { id: 'steam', name: 'Steam', href: null, status: 'soon' },
      { id: 'epic', name: 'Epic Games Store', href: 'https://store.epicgames.com/p/iron-fury-2405f3', status: 'live' },
      { id: 'microsoft', name: 'Microsoft Store', href: 'https://apps.microsoft.com/detail/9pjwt94b5b4h', status: 'live' },
    ],
    features: [
      'Four heroes, one to unlock',
      'Six stages, seven boss fights',
      '1-2 player local co-op',
      'Co-op team attacks',
      '32 achievements, 3 difficulties',
      'English and Spanish',
    ],
    frames: [
      { src: '/reel/iron-fury-1.webp', note: 'Key art: four heroes on the Rainline Docks' },
      { src: '/reel/iron-fury-2.webp', note: 'Co-op on the docks' },
      { src: '/reel/iron-fury-3.webp', note: 'The foundry' },
      { src: '/reel/iron-fury-4.webp', note: 'Highway chase on a moving truck' },
      { src: '/reel/iron-fury-5.webp', note: 'The blackout core' },
    ],
    iconSrc: '/icons/iron-fury.webp',
    image: '/products/iron-fury.webp',
  },
  {
    id: 'matchday-empire',
    category: 'videogames',
    name: 'MATCHDAY EMPIRE',
    tagline: 'Football management RPG',
    short: 'Run a club, sign players, set the tactics and fight for the titles. Every matchday changes the story of your club.',
    desc: "MATCHDAY EMPIRE is a football management game for PC and Mac. Build your squad, read the market, bring through the academy and keep the board happy, then follow every match live on a 2D pitch and change the plan when the game turns. A generated world of 7 countries, 14 divisions and 280 clubs, men's or women's football, an online league for up to 8 managers and local hot-seat play. Plays offline, in English and Spanish.",
    icon: FaFutbol,
    color: 'text-accent-emerald',
    bgColor: 'bg-accent-emerald/10',
    borderColor: 'hover:border-accent-emerald/50',
    btnColor: 'bg-accent-emerald hover:bg-accent-emerald/90',
    link: '/products/matchday-empire',
    siteLink: 'https://matchdayempire.com',
    price: '£6.99',
    priceNote: 'One-time £6.99',
    year: '2026',
    platform: 'Windows + Mac',
    comingSoon: true,
    store: null,
    storeLink: null,
    stores: [
      { id: 'steam', name: 'Steam', href: null, status: 'soon' },
      { id: 'epic', name: 'Epic Games Store', href: null, status: 'soon' },
      { id: 'apple', name: 'Mac App Store', href: null, status: 'soon' },
      { id: 'microsoft', name: 'Microsoft Store', href: 'https://apps.microsoft.com/detail/9mtbz5lq90ss', status: 'live' },
    ],
    features: [
      'Every match live on a 2D pitch',
      'Transfers, scouting and academy',
      '7 countries, 14 divisions, 280 clubs',
      "Men's or women's football",
      'Online league for up to 8 managers',
      'Plays offline, English and Spanish',
    ],
    frames: [
      { src: '/reel/matchday-empire-1.webp', note: 'Every match live on a 2D pitch' },
      { src: '/reel/matchday-empire-2.webp', note: 'Key moments: you scored, now what?' },
      { src: '/reel/matchday-empire-3.webp', note: 'The match plan, chosen before kickoff' },
      { src: '/reel/matchday-empire-4.webp', note: 'The office: finances, board and dressing room' },
      { src: '/reel/matchday-empire-5.webp', note: 'The squad, with form and potential' },
      { src: '/reel/matchday-empire-6.webp', note: 'Tactics and line-up' },
      { src: '/reel/matchday-empire-7.webp', note: 'The transfer market' },
      { src: '/reel/matchday-empire-8.webp', note: 'The league table' },
    ],
    iconSrc: '/icons/matchday-empire.webp',
    image: '/products/matchday-empire.webp',
  },
  {
    id: 'chrono-arena',
    category: 'videogames',
    name: 'Chrono Arena',
    tagline: 'Retro Boomer-Shooter',
    // Short line for cards, long line for the Products page.
    short: 'Four mythic ages, one collapsing labyrinth. Slay god-beasts across Egypt, Greece, Niflheim and Yomi.',
    desc: 'A fast-paced first-person shooter through four mythic ages. Battle through Egypt, Greece, Niflheim and Yomi with 10 unique weapons, Chrono Surge time-bending mechanics, and multiple game modes.',
    icon: FaClock,
    color: 'text-accent-orange',
    bgColor: 'bg-accent-orange/10',
    borderColor: 'hover:border-accent-orange/50',
    btnColor: 'bg-accent-orange hover:bg-accent-orange/90',
    link: '/products/chrono-arena',
    price: '$3.99',
    priceNote: 'One-time $3.99',
    year: '2026',
    platform: 'Windows',
    store: 'microsoft',
    storeLink: 'https://apps.microsoft.com/detail/9P9KMKPHP4NJ',
    features: [
      '4 Mythic Ages to Conquer',
      '10 Unique Mythic Weapons',
      'Chrono Surge Time Mechanic',
      'Campaign + Horde + New Game+',
      'Fully Remappable Controls',
      'Zero Tracking or Ads',
    ],
    frames: [
      { src: '/reel/chrono-arena-1.webp', note: 'Key art — the Egypt gate' },
      { src: '/reel/chrono-arena-2.webp', note: 'In-engine capture — Greece' },
      { src: '/reel/chrono-arena-3.webp', note: 'In-engine capture — Niflheim' },
      { src: '/reel/chrono-arena-4.webp', note: 'In-engine capture — arena combat' },
    ],
    iconSrc: '/icons/chrono-arena.webp',
    image: '/products/chrono-arena.webp',
  },
  {
    id: 'eldritch-descent-x',
    category: 'videogames',
    name: 'Eldritch Descent X',
    tagline: "Cosmic-horror shoot 'em up",
    short: 'Dive through five drowned strata. The more power you take from the Old Ones, the more the game lies to you.',
    desc: "Eldritch Descent X is a vertical shoot 'em up of cosmic horror for 1-2 players, rebuilt from scratch. Dive through five drowned strata, face a guardian halfway down each one and its lord at the bottom, and take corruption for power while your sanity slips: phantoms join the swarm, the HUD lies and the voices whisper. Readable bullets, deep scoring, local co-op, four difficulties, a daily descent and four endings. Six languages, voiced in English and Spanish.",
    icon: FaSkull,
    color: 'text-accent-purple',
    bgColor: 'bg-accent-purple/10',
    borderColor: 'hover:border-accent-purple/50',
    btnColor: 'bg-accent-purple hover:bg-accent-purple/90',
    link: '/products/eldritch-descent-x',
    siteLink: 'https://eldritchx.mediaverselabs.com',
    price: 'Coming soon',
    priceNote: 'Coming soon',
    year: '2026',
    platform: 'Windows',
    comingSoon: true,
    store: null,
    storeLink: null,
    stores: [
      { id: 'steam', name: 'Steam', href: null, status: 'soon' },
      { id: 'epic', name: 'Epic Games Store', href: null, status: 'soon' },
      { id: 'microsoft', name: 'Microsoft Store', href: null, status: 'soon' },
    ],
    features: [
      'Five strata, five guardians',
      'Seven lords, two hidden',
      'Sanity that lies to you',
      '1-2 player local co-op',
      '4 difficulties, daily descent',
      'Six languages, voiced EN/ES',
    ],
    frames: [
      { src: '/reel/eldritch-descent-x-1.webp', note: 'The Silt Mother, guardian of the Abyss' },
      { src: '/reel/eldritch-descent-x-2.webp', note: 'The Void Titan, lord of the fifth stratum' },
      { src: '/reel/eldritch-descent-x-3.webp', note: 'Two pilots in local co-op' },
      { src: '/reel/eldritch-descent-x-4.webp', note: 'Drowned sea mines in the Abyss' },
      { src: '/reel/eldritch-descent-x-5.webp', note: 'The Tide Mirror turns their fire back' },
      { src: '/reel/eldritch-descent-x-6.webp', note: 'A slain lord bursts apart' },
    ],
    iconSrc: '/icons/eldritch-descent-x.webp',
    image: '/products/eldritch-descent-x.webp',
  },
  {
    id: 'eldritch-descent',
    category: 'videogames',
    name: 'Eldritch Descent',
    tagline: 'A Cosmic Horror Shooter',
    short: 'Lovecraftian arcade space shooter. Battle Shoggoths and Ancient Ones across the void.',
    desc: 'Lovecraftian arcade space shooter. Fight Shoggoths, Deep Ones and Ancient Ones across 9 levels. 8 weapons, corruption powers, and Xbox controller support. How deep can you descend?',
    icon: FaSkull,
    color: 'text-accent-purple',
    bgColor: 'bg-accent-purple/10',
    borderColor: 'hover:border-accent-purple/50',
    btnColor: 'bg-accent-purple hover:bg-accent-purple/90',
    link: '/products/eldritch-descent',
    price: '$0.99',
    priceNote: 'One-time $0.99',
    year: '2026',
    platform: 'Windows',
    store: 'microsoft',
    storeLink: 'https://apps.microsoft.com/detail/9N232SC9913R',
    features: [
      '9 Challenging Levels',
      '8 Devastating Weapons',
      'Corruption Power System',
      '3 Difficulty + Endless Mode',
      'Full Controller Support',
      'Zero Tracking or Ads',
    ],
    frames: [
      { src: '/reel/eldritch-descent-1.webp', note: 'Key art — the descent' },
      { src: '/reel/eldritch-descent-2.webp', note: 'Key art — shoggoth swarm' },
      { src: '/reel/eldritch-descent-3.webp', note: 'Key art — the deep ones' },
    ],
    iconSrc: '/icons/eldritch-descent.webp',
    image: '/products/eldritch-descent.webp',
  },
  {
    id: 'catalina',
    category: 'software',
    name: 'Catalina AI Web Builder',
    tagline: 'Five Page Websites for Local Businesses',
    short: 'Turn a trade, a name and a city into a full five page website. Written, designed and ready to publish.',
    desc: 'Catalina builds complete five page websites for local businesses: hairdressers, plumbers, dentists, gyms, restaurants and over a hundred more trades. Pick the trade, type the business details, and Catalina writes the pages, designs the layout, draws the map and handles the SEO. Click any text to edit it. Publish free in one click or download the files and host them anywhere. Bring your own AI key; everything you create stays on your computer.',
    icon: FiGlobe,
    color: 'text-accent-emerald',
    bgColor: 'bg-accent-emerald/10',
    borderColor: 'hover:border-accent-emerald/50',
    btnColor: 'bg-accent-emerald hover:bg-accent-emerald/90',
    link: '/products/catalina',
    price: '$44.99',
    priceNote: 'One-time $44.99',
    year: '2026',
    platform: 'Windows',
    store: 'microsoft',
    storeLink: 'https://apps.microsoft.com/detail/9nt2c1nlv68k',
    features: [
      '104 Trades Built In',
      'Full Site From One Form',
      'Click Any Text to Edit',
      'Free One-Click Publishing',
      'AI Logo & Photo Generation',
      'BYOK: Your Own AI Key',
    ],
    frames: [
      { src: '/reel/catalina-1.webp', note: 'The editor — click any text to change it' },
      { src: '/reel/catalina-2.webp', note: 'A generated site — hairdresser, Watford' },
      { src: '/reel/catalina-3.webp', note: 'Pick the trade — 16 families, 104 trades' },
      { src: '/reel/catalina-4.webp', note: 'Publish — live in one click' },
    ],
    iconSrc: '/icons/catalina.webp',
    image: '/products/catalina.webp',
  },
  {
    id: 'pressverse',
    category: 'software',
    name: 'PressVerse',
    tagline: 'AI-Powered Book Creator for Amazon',
    short: 'Write and publish complete books to Amazon KDP in minutes with your own AI key.',
    desc: 'Write, format, and publish complete books to Amazon KDP in minutes. PressVerse is a professional desktop app for Windows that handles every step of the publishing process: niche research, writing full chapters, designing covers, and generating your Amazon listing. You bring your own AI provider key, and your manuscripts never leave your machine.',
    icon: FiBookOpen,
    color: 'text-accent-emerald',
    bgColor: 'bg-accent-emerald/10',
    borderColor: 'hover:border-accent-emerald/50',
    btnColor: 'bg-accent-emerald hover:bg-accent-emerald/90',
    link: '/products/pressverse',
    price: '$18.99',
    priceNote: 'One-time $18.99',
    year: '2026',
    platform: 'Windows',
    store: 'microsoft',
    storeLink: 'https://apps.microsoft.com/detail/9MXQ3BZ59X2P',
    features: [
      'Full AI book generation',
      'Bring your own AI key',
      'BSR niche research',
      'Paperback cover designer',
      'PDF, EPUB 3 and DOCX export',
      'Literary Critic AI review',
    ],
    frames: [
      { src: '/reel/pressverse-1.webp', note: 'Dashboard — your books at a glance' },
      { src: '/reel/pressverse-2.webp', note: 'Book Editor — write and revise chapters' },
    ],
    iconSrc: '/icons/pressverse.webp',
    image: '/products/pressverse.webp',
  },
  {
    id: 'tvverse',
    category: 'software',
    name: 'TVVerse',
    tagline: 'Your Personal Live TV Hub',
    short: 'The most powerful, minimalist M3U player built exclusively for Windows.',
    desc: 'TVVerse is a powerful, minimalist M3U playlist player designed exclusively for Windows. Bring your own playlists and experience television with a beautifully crafted, clutter-free interface.',
    icon: FiTv,
    color: 'text-accent-red',
    bgColor: 'bg-accent-red/10',
    borderColor: 'hover:border-accent-red/50',
    btnColor: 'bg-accent-red hover:bg-accent-red/90',
    link: '/products/tvverse',
    price: '$3.99',
    priceNote: 'One-time $3.99',
    year: '2025',
    platform: 'Windows + web',
    store: 'microsoft',
    storeLink: 'https://apps.microsoft.com/detail/9phvslkz7mxz',
    features: ['M3U & M3U8 Support', 'EPG Integration', 'Picture-in-Picture', 'Zero Telemetry'],
    frames: [
      { src: '/reel/tvverse-1.webp', note: 'Live TV — 12 channels loaded' },
      { src: '/reel/tvverse-2.webp', note: 'My Lists — bring your own M3U' },
      { src: '/reel/tvverse-3.webp', note: 'About' },
      { src: '/reel/tvverse-4.webp', note: 'Explore the Verse' },
    ],
    iconSrc: '/icons/tvverse.webp',
    image: '/products/tvverse.webp',
  },
  {
    id: 'radioverse',
    category: 'software',
    name: 'RadioVerse',
    tagline: 'Global Frequencies, Local App',
    short: 'Thousands of internet radio stations and podcasts with zero tracking.',
    desc: 'Tune into thousands of internet radio stations and podcasts from around the world. RadioVerse categorizes stations by genre, country, and language, all wrapped in a sleek, dark-themed UI.',
    icon: FiRadio,
    color: 'text-accent-blue',
    bgColor: 'bg-accent-blue/10',
    borderColor: 'hover:border-accent-blue/50',
    btnColor: 'bg-accent-blue hover:bg-accent-blue/90',
    link: '/products/radioverse',
    price: '$3.99',
    priceNote: 'One-time $3.99',
    year: '2025',
    platform: 'Windows',
    store: 'microsoft',
    storeLink: 'https://apps.microsoft.com/detail/9pkx5kdcfxl1',
    features: ['50,000+ Global Stations', 'High-Quality Audio', 'Sleep Timer', 'Custom Station URLs'],
    frames: [
      // Two views of the one capture I could take. A third frame re-cropping the
      // same table ghosted against the overview during the cross-fade — a zoom of
      // an image is not a new frame.
      { src: '/reel/radioverse-1.webp', note: 'Live Radio — 50,006 stations loaded' },
      { src: '/reel/radioverse-3.webp', note: 'Built-in player, favourites and filters' },
    ],
    iconSrc: '/icons/radioverse.webp',
    image: '/products/radioverse.webp',
  },
  {
    id: 'promptwick',
    category: 'extensions',
    name: 'PromptWick',
    tagline: 'Prompt Improver for Chrome',
    short: 'Rewrites a rough prompt into a clear, structured one without leaving the chat box.',
    desc: 'PromptWick sits in the Chrome side panel and rewrites your rough prompt into a clear, structured one, right where you are already typing. It works on ChatGPT, Claude, Gemini, Grok, Perplexity, DeepSeek and five more, with a reusable template library, saved variables and a full prompt history.',
    icon: FiFeather,
    color: 'text-accent-amber',
    bgColor: 'bg-accent-amber/10',
    borderColor: 'hover:border-accent-amber/50',
    btnColor: 'bg-accent-amber hover:bg-accent-amber/90',
    link: '/products/promptwick',
    price: 'Free',
    priceNote: 'Free plan, paid tiers from $6.99/mo',
    year: '2026',
    platform: 'Chrome',
    store: 'chrome',
    storeLink: 'https://chromewebstore.google.com/detail/promptwick-prompt-improve/gmjbpablndfijfiflniajddmlfhdmcia',
    siteLink: 'https://promptwick.com',
    features: [
      'Works on 11 AI sites',
      'Native Chrome side panel',
      'Template and variable library',
      'Deep multi-pass rewrite',
      'Full prompt history',
      'No ads, no resold data',
    ],
    frames: [
      { src: '/products/promptwick.svg', note: 'Rough prompt in, structured prompt out' },
    ],
    iconSrc: '/icons/promptwick.webp',
    image: '/products/promptwick.svg',
  },
  {
    id: 'myrankflow',
    category: 'saas',
    name: 'MyRankFlow',
    tagline: 'White-label backlink agency',
    short: 'A rebrandable SEO storefront: your clients order backlinks, the platform delivers them, you keep the margin.',
    desc: 'MyRankFlow is a white-label platform for reselling SEO backlinks under your own brand. You get a rebrandable storefront with over 25 link services, automated delivery and CSV reporting, integrated payments and a live stats dashboard. Your clients never see us. Sold as a one-time licence rather than a monthly platform fee.',
    icon: FiTrendingUp,
    color: 'text-signal',
    bgColor: 'bg-signal/10',
    borderColor: 'hover:border-signal/50',
    btnColor: 'bg-signal hover:bg-signal/90',
    link: '/products/myrankflow',
    price: '$47',
    priceNote: 'One-time $47',
    year: '2026',
    platform: 'Web',
    store: null,
    storeLink: null,
    siteLink: 'https://myrankflow.com',
    features: [
      '25+ backlink services',
      'Your brand, not ours',
      'Automated delivery',
      'CSV client reports',
      'Payments built in',
      'No monthly platform fee',
    ],
    frames: [
      { src: '/reel/myrankflow-1.webp', note: 'The storefront your clients see, under your brand' },
    ],
    iconSrc: '/icons/myrankflow.svg',
    image: '/products/myrankflow.webp',
  },
  {
    id: 'crxify',
    category: 'saas',
    name: 'Crxify',
    tagline: 'AI Chrome extension builder',
    short: 'Turn a sentence into a real Chrome extension. Describe it, preview it, and download it — no code required.',
    desc: 'Crxify is a hosted web app that turns a plain-language description into a real, working Chrome extension. Describe what you want it to do, watch Crxify write the manifest and the code, ask for changes in natural language, and load the finished extension straight into Chrome. No coding, no boilerplate, no manifest files to hand-edit.',
    icon: FiCode,
    color: 'text-signal',
    bgColor: 'bg-signal/10',
    borderColor: 'hover:border-signal/50',
    btnColor: 'bg-signal hover:bg-signal/90',
    link: '/products/crxify',
    price: 'Free to start',
    priceNote: 'Free to start · current pricing on crxify.com',
    year: '2026',
    platform: 'Web',
    store: null,
    storeLink: null,
    siteLink: 'https://crxify.com',
    features: [
      'Describe it in plain English',
      'AI writes the manifest and code',
      'Natural-language edits',
      'Load straight into Chrome',
    ],
    frames: [
      { src: '/reel/crxify-1.webp', note: 'Build with AI — one instruction, four real extension files' },
    ],
    iconSrc: '/icons/crxify.svg',
    image: '/products/crxify.webp',
  },
  {
    id: 'smart-memory-saver',
    category: 'extensions',
    name: 'Smart Memory Saver',
    tagline: 'Tab sleeper for Chrome',
    short: 'Puts idle tabs to sleep to free memory, and keeps them in your tab strip so one click brings them back.',
    desc: 'Chrome slows down when you keep forty tabs open, and closing them means losing your place. Smart Memory Saver takes the third option: idle tabs are put to sleep so they stop using memory, and they stay in your tab strip exactly where you left them. It makes no network requests at all — the manifest sets connect-src to none, so the browser itself blocks outbound connections rather than us merely promising not to make any.',
    icon: FiMoon,
    color: 'text-signal',
    bgColor: 'bg-signal/10',
    borderColor: 'hover:border-signal/50',
    btnColor: 'bg-signal hover:bg-signal/90',
    link: '/products/smart-memory-saver',
    price: 'Free',
    priceNote: 'Free, no ads',
    year: '2026',
    platform: 'Chrome',
    store: 'chrome',
    storeLink: 'https://chromewebstore.google.com/detail/smart-memory-saver/plbboiloifgjjlaaoflkkapobbcdkhek',
    features: [
      'Sleeps only idle tabs',
      'Never the tab you are on',
      'Pinned, audio and calls protected',
      'Your own never-sleep list',
      'Manual, Balanced or Aggressive',
      'Zero network requests',
    ],
    frames: [
      { src: '/reel/smart-memory-saver-1.webp', note: 'The dashboard — in use, idle, asleep, kept awake' },
      { src: '/reel/smart-memory-saver-2.webp', note: 'Sleep every idle tab in one click' },
      { src: '/reel/smart-memory-saver-3.webp', note: 'Every protected tab says why' },
      { src: '/reel/smart-memory-saver-4.webp', note: 'Your never-sleep list and the defaults' },
      { src: '/reel/smart-memory-saver-5.webp', note: 'Manual mode, and what it will not do' },
    ],
    iconSrc: '/icons/smart-memory-saver.webp',
    image: '/products/smart-memory-saver.webp',
  },
  {
    id: 'my-online-radio',
    category: 'extensions',
    name: 'My Online Radio',
    tagline: 'Internet Radio Player for Chrome',
    short: 'Search 58,000+ internet radio stations by name or country and stream them from the toolbar, favorites saved.',
    desc: 'My Online Radio searches the community-run Radio Browser catalog, over 58,000 internet radio stations from every country, and streams whichever one you pick straight from the toolbar. Star a station to save it, filter by country, and keep listening after you close the popup: playback lives in a background document, so leaving the popup never cuts the stream. No accounts, no ads, and only three fixed API hosts as host permissions — it never reads anything on the pages you visit.',
    icon: FiRadio,
    color: 'text-accent-indigo',
    bgColor: 'bg-accent-indigo/10',
    borderColor: 'hover:border-accent-indigo/50',
    btnColor: 'bg-accent-indigo hover:bg-accent-indigo/90',
    link: '/products/my-online-radio',
    price: 'Free',
    priceNote: 'Free, no ads',
    year: '2026',
    platform: 'Chrome',
    store: 'chrome',
    storeLink: 'https://chromewebstore.google.com/detail/my-online-radio/mmimoninabdklbgpjonajpidfjljojlp',
    features: [
      '58,000+ stations worldwide',
      'Search by name or country',
      'Keeps playing, popup closed',
      'Favorites saved on-device',
      'No accounts, no ads',
      'Only 3 fixed API hosts',
    ],
    frames: [
      { src: '/reel/my-online-radio-1.webp', note: 'Search thousands of stations by name or country' },
      { src: '/reel/my-online-radio-2.webp', note: 'Listen without limits — keeps playing, popup closed' },
      { src: '/reel/my-online-radio-3.webp', note: 'Save your favorites with one star' },
      { src: '/reel/my-online-radio-4.webp', note: 'Your favorites, always ready in their own tab' },
      { src: '/reel/my-online-radio-5.webp', note: 'Filter by country and hear its most popular stations' },
    ],
    iconSrc: '/icons/my-online-radio.webp',
    image: '/products/my-online-radio.webp',
  },
  {
    id: 'nano-banana-visuals',
    category: 'launches',
    name: 'Nano Banana Visuals Domination',
    tagline: 'AI Visuals Toolkit for Creators',
    short: 'Create 30 types of professional visuals with Google\'s Nano Banana in seconds: a guide plus a Prompt Builder web app.',
    desc: 'A step-by-step system for creating professional visuals with Nano Banana, the image model inside Gemini. Thirty visual types, from YouTube thumbnails and ads to book covers, wall art and menus, each with a master prompt, a pro tip and real examples. The Prompt Builder web app writes the prompt for you: pick a type, fill in a few fields, paste into Gemini. Works with a free Google account.',
    icon: FiImage,
    color: 'text-accent-amber',
    bgColor: 'bg-accent-amber/10',
    borderColor: 'hover:border-accent-amber/50',
    btnColor: 'bg-accent-amber hover:bg-accent-amber/90',
    // A launch product with its own site: no page here, the row links straight out.
    link: null,
    siteLink: 'https://visuals.mediaverselabs.com',
    price: '$9.95',
    priceNote: 'One-time $9.95 (launch price)',
    year: '2026',
    platform: 'Web',
    store: null,
    storeLink: null,
    status: 'Launching',
    features: [
      '30 Professional Visual Types',
      'Prompt Builder Web App',
      'Quick-Start Mini-Course',
      '3 Cheat Sheets',
      'Works With Free Gemini',
      'No Design Skills Needed',
    ],
    frames: [],
    iconSrc: '/icons/nano-banana-visuals.webp',
    image: '/products/nano-banana-visuals.webp',
  },
];

export const byCategory = (id) => PRODUCTS.filter((p) => p.category === id);

// Detail pages used to hardcode their own image path, which is how Chrono Arena
// ended up showing Eldritch Descent and how deleting the old JPEGs broke five pages
// at once. They look the product up here instead.
export const getProduct = (id) => PRODUCTS.find((p) => p.id === id);
